import React from 'react'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import {
  Typography,
  Chip,
  CircularProgress,
  Snackbar,
  LinearProgress
} from '@material-ui/core'
import {
  PictureAsPdf,
  Description as DescriptionIcon,
  InsertDriveFile,
  Image as ImageIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Tune as TuneIcon,
  Visibility as VisibilityIcon
} from '@material-ui/icons'
import DocumentUploadModal from './DocumentUploadModal'
import TakeoffRequestModal from './TakeoffRequestModal'
import ImportPreviewModal from './ImportPreviewModal'
import PlanPageSelector from './PlanPageSelector'
import VisualReviewModal from './VisualReviewModal'
import ExtractionRulesDrawer from './ExtractionRulesDrawer'
import ContextButton from '../shared/ContextButton'
import ContextIndicator from '../shared/ContextIndicator'
import { useBidConfirmation } from '../shared/BidConfirmationContext'

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (e) {
    return ''
  }
}

const formatElapsed = (value) => {
  const totalSeconds = Math.max(0, Math.floor(Number(value) || 0))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes <= 0) return `${seconds}s`
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`
}

const iconForFileType = (fileType) => {
  switch ((fileType || '').toLowerCase()) {
    case 'pdf':
      return <PictureAsPdf style={{ color: '#d32f2f', fontSize: 32 }} />
    case 'xlsx':
    case 'xls':
    case 'csv':
      return <DescriptionIcon style={{ color: '#2e7d32', fontSize: 32 }} />
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return <ImageIcon style={{ color: '#1976d2', fontSize: 32 }} />
    default:
      return <InsertDriveFile style={{ color: '#757575', fontSize: 32 }} />
  }
}

const parseStatusChip = (status) => {
  switch (status) {
    case 'parsed':
      return { label: 'Parsed', style: { backgroundColor: '#c8e6c9', color: '#2e7d32' } }
    case 'classified':
      return { label: 'Plan Set — Select Pages', style: { backgroundColor: '#e3f2fd', color: '#1565c0' } }
    case 'failed':
      return { label: 'Parse failed', style: { backgroundColor: '#ffcdd2', color: '#c62828' } }
    case 'unsupported':
      return { label: 'Unsupported', style: { backgroundColor: '#eeeeee', color: '#616161' } }
    case 'pending':
      return { label: 'Parsing…', style: { backgroundColor: '#fff9c4', color: '#f57f17' } }
    case 'not_attempted':
    default:
      return { label: 'Not parsed', style: { backgroundColor: '#f5f5f5', color: '#757575' } }
  }
}

const getDocumentPageMetrics = (doc) => {
  const pageSummary = doc && doc.pageSummary
  if (pageSummary && Number.isFinite(Number(pageSummary.totalPages))) {
    return {
      totalPages: Number(pageSummary.totalPages || 0),
      schedulePages: Number(pageSummary.schedulePages || 0)
    }
  }
  const sheetIndex = doc && doc.parseResult && Array.isArray(doc.parseResult.sheetIndex) ? doc.parseResult.sheetIndex : []
  return {
    totalPages: sheetIndex.length,
    schedulePages: sheetIndex.filter(s => s.hasSchedule).length
  }
}

const CATEGORY_CHIP = {
  plans:         { label: 'Plans',        bg: 'var(--info-tint, #e3f2fd)',     color: 'var(--info, #1565c0)' },
  bid_response:  { label: 'Bid Response', bg: 'var(--accent-tint, #fff3e0)',   color: 'var(--accent, #e65100)' },
  vendor_quote:  { label: 'Vendor Quote', bg: 'var(--ok-tint, #e8f5e9)',       color: 'var(--ok, #2e7d32)' }
}

const DocumentsSection = ({ projectId, project, url, onRefresh, onTriggerUpload, bidPackages, onDeleteBidPackage, onOpenContextDrawer }) => {
  const { openBidConfirmation } = useBidConfirmation()
  const history = useHistory()
  const hydratedProjectRef = React.useRef(null)
  const [documents, setDocuments] = React.useState(
    (project && Array.isArray(project.documents)) ? project.documents : []
  )
  const [loading, setLoading] = React.useState(false)
  const [showUploadModal, setShowUploadModal] = React.useState(false)
  const [importDoc, setImportDoc] = React.useState(null) // { _id, parseResult }
  const [pageSelectorDoc, setPageSelectorDoc] = React.useState(null) // doc with classified parseResult
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '' })
  const [takeoffDoc, setTakeoffDoc] = React.useState(null)
  const [rulesDrawer, setRulesDrawer] = React.useState({ open: false, mode: 'manager' })
  const [viewPagesDoc, setViewPagesDoc] = React.useState(null) // doc to view page images for
  const [pendingParseDocIds, setPendingParseDocIds] = React.useState(() => new Set())
  const [jobClock, setJobClock] = React.useState(() => Date.now())
  const [contextCounts, setContextCounts] = React.useState({})

  const upsertLiveJobState = React.useCallback((docId, patch = {}) => {
    if (!docId) return
    setDocuments(prev => prev.map(doc => {
      if (String(doc._id) !== String(docId)) return doc
      const currentJob = doc.latestJob || {}
      const nextJob = {
        ...currentJob,
        ...patch,
        startedAt: patch.startedAt || currentJob.startedAt || new Date().toISOString()
      }
      return {
        ...doc,
        parseStatus: patch.parseStatus || doc.parseStatus,
        latestJob: nextJob
      }
    }))
  }, [])

  const trackPendingDoc = React.useCallback((docId) => {
    if (!docId) return
    setPendingParseDocIds(prev => {
      const next = new Set(prev)
      next.add(String(docId))
      return next
    })
  }, [])

  const untrackPendingDoc = React.useCallback((docId) => {
    if (!docId) return
    setPendingParseDocIds(prev => {
      const next = new Set(prev)
      next.delete(String(docId))
      return next
    })
  }, [])

  const fetchDocuments = React.useCallback(() => {
    if (!projectId) return
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents`).then(res => {
      setDocuments(Array.isArray(res.data) ? res.data : [])
      setLoading(false)
    }).catch(err => {
      console.error('[DocumentsSection] fetch error:', err)
      setLoading(false)
    })
  }, [projectId, url])

  React.useEffect(() => {
    const projectKey = String(projectId || '')
    const projectChanged = hydratedProjectRef.current !== projectKey

    if (project && Array.isArray(project.documents)) {
      if (projectChanged || documents.length === 0) {
        setDocuments(project.documents)
        hydratedProjectRef.current = projectKey
      }
      return
    }

    if (projectChanged || documents.length === 0) {
      fetchDocuments()
      hydratedProjectRef.current = projectKey
    }
  }, [projectId, project, fetchDocuments, documents.length])

  // Listen for socket events to auto-refresh when extraction completes
  React.useEffect(() => {
    if (!projectId) return
    try {
      const { subscribe } = require('../../SocketIo/Socket')
      const unsub = subscribe(`doc_upload_${projectId}`, (data) => {
        if (!data || !data.stage) return

        const eventDocId = data.docId || data.documentId
        const isTracked = eventDocId && pendingParseDocIds.has(String(eventDocId))
        const progressCurrent = Number(data.page || 0)
        const progressTotal = Number(data.total || 0)
        const nextStatus = data.stage === 'complete'
          ? (
              data.parseStatus === 'failed'
                ? 'failed'
                : data.parseStatus === 'parsed'
                  ? 'completed'
                  : data.parseStatus === 'classified'
                    ? 'completed'
                    : 'completed'
            )
          : 'running'

        if (eventDocId) {
          upsertLiveJobState(eventDocId, {
            status: nextStatus,
            stage: data.stage,
            message: data.message || '',
            progressCurrent,
            progressTotal,
            parseStatus: data.parseStatus || undefined
          })
        }

        if (data.stage === 'complete') {
          if (
            isTracked ||
            data.parseStatus === 'classified' ||
            data.parseStatus === 'parsed' ||
            data.parseStatus === 'failed'
          ) {
            fetchDocuments()
          }
          if (isTracked && eventDocId) untrackPendingDoc(eventDocId)
          if (
            isTracked ||
            data.parseStatus === 'classified' ||
            data.parseStatus === 'parsed' ||
            data.parseStatus === 'failed'
          ) {
            setSnackbar({
              open: true,
              message:
                data.message ||
                (data.parseStatus === 'classified'
                  ? 'Classification complete.'
                  : data.parseStatus === 'parsed'
                    ? 'Extraction complete.'
                    : 'Extraction failed.')
            })
          }
          return
        }

      })
      return unsub
    } catch (e) { /* socket not available */ }
  }, [projectId, fetchDocuments, onRefresh, pendingParseDocIds, untrackPendingDoc, upsertLiveJobState])


  // Fetch context counts for all documents in a single batch
  React.useEffect(() => {
    if (!documents?.length || !projectId) return
    const parents = documents.map(d => ({ parent_type: 'document', parent_id: d._id }))
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/context/counts`, { parents })
      .then(res => setContextCounts(res.data))
      .catch(() => {})
  }, [documents, projectId, url])

  const handleUploaded = React.useCallback((newDoc) => {
    if (newDoc && newDoc._id) {
      setDocuments(prev => [...prev, {
        ...newDoc,
        latestJob: newDoc.parseStatus === 'pending'
          ? {
              status: 'running',
              stage: 'queued',
              message: 'Upload complete. Classifying pages in background...',
              progressCurrent: 0,
              progressTotal: 0,
              startedAt: new Date().toISOString()
            }
          : newDoc.latestJob
      }])
      if (newDoc.parseStatus === 'pending') {
        trackPendingDoc(newDoc._id)
        setSnackbar({
          open: true,
          message: 'Upload complete. Classifying pages in background...'
        })
      }
    } else {
      fetchDocuments()
    }
  }, [fetchDocuments, trackPendingDoc])

  React.useEffect(() => {
    if (typeof window === 'undefined' || !projectId) return undefined

    const handleDocumentUploadedEvent = (event) => {
      const detail = event?.detail
      if (!detail || String(detail.projectId) !== String(projectId)) return
      handleUploaded(detail.payload)
    }

    window.addEventListener('bid-estimation-document-uploaded', handleDocumentUploadedEvent)
    return () => window.removeEventListener('bid-estimation-document-uploaded', handleDocumentUploadedEvent)
  }, [projectId, handleUploaded])

  const handleDelete = (doc) => {
    if (!doc || !doc._id) return
    openBidConfirmation({
      title: 'Delete Document',
      message: `Delete "${doc.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      tone: 'danger',
      onConfirm: () => {
        axios.delete(`${url}/api/bid-estimation/projects/${projectId}/documents/${doc._id}`).then(() => {
          const deletedDocId = String(doc._id)
          setDocuments(prev => prev.filter(d => String(d._id) !== deletedDocId))
          setContextCounts(prev => {
            const next = { ...prev }
            delete next[`document:${deletedDocId}`]
            return next
          })
          untrackPendingDoc(deletedDocId)
          onRefresh && onRefresh()
          setSnackbar({ open: true, message: 'Document deleted' })
        }).catch(err => {
          console.error('[DocumentsSection] delete error:', err)
          setSnackbar({
            open: true,
            message: err.response?.data?.error || 'Failed to delete document'
          })
        })
      }
    })
  }

  const findVendorQuoteIdForDocument = React.useCallback(async (doc) => {
    if (doc.vendorQuoteId) return doc.vendorQuoteId

    const { data } = await axios.get(`${url}/api/bid-estimation/vendor-quotes/project/${projectId}`)
    const quotes = Array.isArray(data) ? data : []
    const match = quotes.find(q => (
      (doc.s3Key && q.s3Key && String(q.s3Key) === String(doc.s3Key)) ||
      (doc.name && q.originalFilename && String(q.originalFilename) === String(doc.name))
    ))
    return match?._id || null
  }, [projectId, url])

  const handleImportClick = async (doc) => {
    if (doc.category === 'vendor_quote') {
      try {
        const quoteId = await findVendorQuoteIdForDocument(doc)
        if (!quoteId) {
          setSnackbar({ open: true, message: 'Could not find linked vendor quote' })
          return
        }
        history.push(`/bid-estimation/${projectId}/vendor-quotes?quoteId=${quoteId}`)
      } catch (err) {
        console.error('[DocumentsSection] vendor quote lookup error:', err)
        setSnackbar({ open: true, message: 'Could not open vendor quote' })
      }
      return
    }

    axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${doc._id}/view`).then(res => {
      setImportDoc(res.data)
    }).catch(err => {
      console.error('[DocumentsSection] document-view fetch error:', err)
      setSnackbar({ open: true, message: 'Could not load document view' })
    })
  }

  const handleOpenPageSelector = (doc) => {
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${doc._id}/view`).then(res => {
      const view = res.data || {}
      setPageSelectorDoc({
        ...doc,
        ...view.document,
        parseResult: view.parseResult || doc.parseResult || null,
        pages: Array.isArray(view.pages) ? view.pages : []
      })
    }).catch(err => {
      console.error('[DocumentsSection] document-view fetch error:', err)
      setSnackbar({ open: true, message: 'Could not load classified pages' })
    })
  }

  const handleImported = (count, result) => {
    const blockedCount = Array.isArray(result?.blockedRows) ? result.blockedRows.length : 0
    const suffix = blockedCount > 0
      ? `; ${blockedCount} row${blockedCount === 1 ? '' : 's'} left for review`
      : ''
    setSnackbar({ open: true, message: `Imported ${count} item${count === 1 ? '' : 's'}${suffix}` })
  }

  const handleQuickExtract = async (docId, pages) => {
    try {
      const { data } = await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/parse-pages`,
        { pages, postExtractMode: 'manual_review' }
      )
      setDocuments(prev => prev.map(d => d._id === docId
        ? {
            ...d,
            parseStatus: 'pending',
            latestJob: {
              ...(d.latestJob || {}),
              status: 'running',
              stage: 'queued',
              message: data?.message || `Extracting ${pages.length} pages in background...`,
              progressCurrent: 0,
              progressTotal: pages.length || 0,
              startedAt: new Date().toISOString()
            }
          }
        : d))
      trackPendingDoc(docId)
      setSnackbar({
        open: true,
        message: data?.message || `Extracting ${pages.length} pages in background...`
      })
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to start extraction' })
    }
  }

  const handlePagesParsed = (updatedDoc) => {
    fetchDocuments()
    setPageSelectorDoc(null)
    setSnackbar({ open: true, message: 'Pages extracted! Click "Import Items" to add them.' })
  }

  const handleParseStarted = ({ docId, pages, message }) => {
    if (!docId) return
    setDocuments(prev => prev.map(d => d._id === docId
      ? {
          ...d,
          parseStatus: 'pending',
          latestJob: {
            ...(d.latestJob || {}),
            status: 'running',
            stage: 'queued',
            message: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`,
            progressCurrent: 0,
            progressTotal: Array.isArray(pages) ? pages.length : 0,
            startedAt: new Date().toISOString()
          }
        }
      : d))
    trackPendingDoc(docId)
    setSnackbar({
      open: true,
      message: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`
    })
  }

  const activeJobs = React.useMemo(() => {
    const now = jobClock
    return documents
      .map(doc => {
        const latestJob = doc.latestJob || null
        if (!latestJob) return null
        const status = String(latestJob.status || '').toLowerCase()
        const activeStatuses = new Set(['queued', 'running', 'leased', 'retrying', 'processing'])
        const isActive = activeStatuses.has(status) || doc.parseStatus === 'pending'
        if (!isActive) return null
        const startedAt = latestJob.startedAt ? new Date(latestJob.startedAt).getTime() : null
        const elapsedSeconds = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0
        return {
          docId: doc._id,
          name: doc.name,
          parseStatus: doc.parseStatus,
          message: latestJob.message || 'Working...',
          stage: latestJob.stage || '',
          progressCurrent: Number(latestJob.progressCurrent || 0),
          progressTotal: Number(latestJob.progressTotal || 0),
          elapsedSeconds
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [documents, jobClock])

  React.useEffect(() => {
    if (activeJobs.length === 0) return undefined
    const timer = window.setInterval(() => {
      setJobClock(Date.now())
    }, 1000)
    return () => window.clearInterval(timer)
  }, [activeJobs.length])

  return (
    <div className="hmi-panel">
      <div className="hmi-panel-head">
        <h4>Documents{documents.length > 0 && <span style={{ fontWeight: 400, color: 'var(--ink-4)', marginLeft: 6 }}>({documents.length})</span>}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            className="hmi-btn hmi-btn-ghost hmi-btn-sm"
            onClick={() => setRulesDrawer({ open: true, mode: 'manager' })}
            title="Extraction Rules"
          >
            <TuneIcon style={{ fontSize: 15 }} />
          </button>
          <button
            className="hmi-btn hmi-btn-primary hmi-btn-sm"
            onClick={() => onTriggerUpload ? onTriggerUpload() : setShowUploadModal(true)}
          >
            + Upload
          </button>
        </div>
      </div>
      <div className="hmi-panel-body" style={{ padding: '8px 12px' }}>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress size={24} />
        </div>
      )}

      {!loading && documents.length === 0 && !(Array.isArray(bidPackages) && bidPackages.length > 0) && (
        <div
          onClick={() => onTriggerUpload ? onTriggerUpload() : setShowUploadModal(true)}
          style={{
            padding: '28px 16px',
            textAlign: 'center',
            color: 'var(--ink-4)',
            border: '1px dashed var(--line-2, #d0d0d0)',
            borderRadius: 6,
            cursor: 'pointer',
            margin: '4px 0'
          }}
        >
          <div style={{ fontSize: 13 }}>No documents yet — click to upload</div>
        </div>
      )}

      {!loading && (documents.length > 0 || (Array.isArray(bidPackages) && bidPackages.length > 0)) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {documents.map(doc => {
            const chip = parseStatusChip(doc.parseStatus)
            const pageMetrics = getDocumentPageMetrics(doc)
            const latestJob = doc.latestJob || null
            const catChip = CATEGORY_CHIP[doc.category]
            return (
              <div
                key={doc._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 6px',
                  borderRadius: 6,
                  transition: 'background 0.1s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-panel-alt, #f7f7f7)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* File icon — smaller, subtler */}
                <div style={{ flexShrink: 0, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {React.cloneElement(iconForFileType(doc.fileType), { style: { ...iconForFileType(doc.fileType).props.style, fontSize: 22 } })}
                </div>

                {/* Info block */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <a
                      href={doc.s3Url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'var(--ink)',
                        fontWeight: 600,
                        textDecoration: 'none',
                        fontSize: 13,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {doc.name}
                    </a>
                    {catChip && (
                      <span style={{
                        fontSize: 9, fontWeight: 600, letterSpacing: '0.04em',
                        padding: '1px 7px', borderRadius: 10,
                        background: catChip.bg, color: catChip.color,
                        whiteSpace: 'nowrap', textTransform: 'uppercase'
                      }}>
                        {catChip.label}
                      </span>
                    )}
                    <span style={{
                      fontSize: 9, fontWeight: 600, letterSpacing: '0.02em',
                      padding: '1px 7px', borderRadius: 10,
                      background: chip.style.backgroundColor, color: chip.style.color,
                      whiteSpace: 'nowrap'
                    }}>
                      {chip.label}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 2, alignItems: 'center', fontSize: 11, color: 'var(--ink-4)' }}>
                    <span>{formatBytes(doc.sizeBytes)}</span>
                    <span>{formatDate(doc.uploadedAt || doc.createdAt)}</span>
                    {pageMetrics.totalPages > 0 && (
                      <span>{pageMetrics.totalPages} pg</span>
                    )}
                    {doc.parseStatus === 'pending' && latestJob && latestJob.message && (
                      <span style={{ color: 'var(--warn, #f57f17)' }}>
                        {latestJob.message}
                        {latestJob.progressTotal > 0 ? ` (${latestJob.progressCurrent || 0}/${latestJob.progressTotal})` : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                  {doc.parseStatus === 'classified' && pageMetrics.totalPages > 0 && (
                    <button
                      className="hmi-btn hmi-btn-sm hmi-btn-outline"
                      style={{ fontSize: 11 }}
                      disabled={doc.parseStatus === 'pending'}
                      onClick={() => handleOpenPageSelector(doc)}
                    >
                      Select Pages
                    </button>
                  )}
                  {doc.parseStatus === 'parsed' && (
                    <>
                      <button
                        className="hmi-btn hmi-btn-sm hmi-btn-outline"
                        style={{ fontSize: 11 }}
                        onClick={() => handleImportClick(doc)}
                      >
                        {doc.category === 'vendor_quote' ? 'Review Quote' : 'Import'}
                      </button>
                      {doc.category === 'plans' && pageMetrics.totalPages > 0 && (
                        <button
                          className="hmi-btn hmi-btn-sm hmi-btn-ghost"
                          style={{ fontSize: 11, color: 'var(--ok, #2e7d32)' }}
                          title="Map It — Draw on plan pages to quantify items"
                          onClick={() => history.push(`/bid-estimation/${projectId}/plan-takeoff/${doc._id}`)}
                        >
                          Map It
                        </button>
                      )}
                      {pageMetrics.totalPages > 0 && (
                        <button
                          className="hmi-btn hmi-btn-sm hmi-btn-ghost"
                          style={{ padding: '0 4px' }}
                          title="View Pages"
                          onClick={() => setViewPagesDoc(doc)}
                        >
                          <VisibilityIcon style={{ fontSize: 15, color: 'var(--ink-3)' }} />
                        </button>
                      )}
                    </>
                  )}
                  {doc.category === 'plans' && (
                    <button
                      className="hmi-btn hmi-btn-sm hmi-btn-ghost"
                      style={{ padding: '0 4px' }}
                      title="Request Takeoff"
                      onClick={() => setTakeoffDoc(doc)}
                    >
                      <SendIcon style={{ fontSize: 15, color: 'var(--ink-3)' }} />
                    </button>
                  )}
                  {contextCounts[`document:${doc._id}`] && (
                    <ContextIndicator
                      hasBlocker={contextCounts[`document:${doc._id}`].hasBlocker}
                      noteCount={contextCounts[`document:${doc._id}`].noteCount}
                      hasReview={contextCounts[`document:${doc._id}`].hasReview}
                    />
                  )}
                  <ContextButton
                    parentType="document"
                    parentId={doc._id}
                    parentLabel={doc.name}
                    onOpenDrawer={onOpenContextDrawer}
                  />
                  <button
                    className="hmi-btn hmi-btn-sm hmi-btn-ghost"
                    style={{ padding: '0 4px' }}
                    title="Delete"
                    onClick={() => handleDelete(doc)}
                  >
                    <DeleteIcon style={{ fontSize: 15, color: 'var(--ink-4)' }} />
                  </button>
                </div>
              </div>
            )
          })}

          {/* Bid Response packages (PDFs stored on the bid package, not in project.documents) */}
          {Array.isArray(bidPackages) && bidPackages.map(pkg => {
            const sf = pkg.sourceFile || {}
            const catChip = CATEGORY_CHIP.bid_response
            return (
              <div
                key={`bp-${pkg._id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 6px',
                  borderRadius: 6,
                  transition: 'background 0.1s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-panel-alt, #f7f7f7)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ flexShrink: 0, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PictureAsPdf style={{ color: '#d32f2f', fontSize: 22 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {sf.s3Url ? (
                      <a href={sf.s3Url} target="_blank" rel="noopener noreferrer"
                        style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'none', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pkg.name}
                      </a>
                    ) : (
                      <span style={{ color: 'var(--ink)', fontWeight: 600, fontSize: 13 }}>{pkg.name}</span>
                    )}
                    <span style={{
                      fontSize: 9, fontWeight: 600, letterSpacing: '0.04em',
                      padding: '1px 7px', borderRadius: 10,
                      background: catChip.bg, color: catChip.color,
                      whiteSpace: 'nowrap', textTransform: 'uppercase'
                    }}>
                      {catChip.label}
                    </span>
                    <span style={{
                      fontSize: 9, fontWeight: 600, letterSpacing: '0.02em',
                      padding: '1px 7px', borderRadius: 10,
                      background: pkg.status === 'mapped' ? 'var(--ok-tint, #e8f5e9)' : 'var(--bg-sunken, #f0f0f0)',
                      color: pkg.status === 'mapped' ? 'var(--ok, #2e7d32)' : 'var(--ink-3)',
                      whiteSpace: 'nowrap'
                    }}>
                      {pkg.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 2, alignItems: 'center', fontSize: 11, color: 'var(--ink-4)' }}>
                    {sf.uploadedAt && <span>{formatDate(sf.uploadedAt)}</span>}
                    {pkg.groups && <span>{pkg.groups.length} group{pkg.groups.length !== 1 ? 's' : ''}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                  <button
                    className="hmi-btn hmi-btn-sm hmi-btn-outline"
                    style={{ fontSize: 11 }}
                    onClick={() => history.push(`/bid-estimation/${projectId}/bid-response/${pkg._id}`)}
                  >
                    Review
                  </button>
                  {onDeleteBidPackage && (
                    <button
                      className="hmi-btn hmi-btn-sm hmi-btn-ghost"
                      style={{ padding: '0 4px' }}
                      title="Delete"
                      onClick={() => onDeleteBidPackage(pkg)}
                    >
                      <DeleteIcon style={{ fontSize: 15, color: 'var(--ink-4)' }} />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
      </div>

      <DocumentUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploaded={handleUploaded}
        projectId={projectId}
        url={url}
      />

      {importDoc && (
        <ImportPreviewModal
          open={Boolean(importDoc)}
          onClose={() => setImportDoc(null)}
          onParseStarted={handleParseStarted}
          onImported={(count, result) => {
            setImportDoc(null)
            handleImported(count, result)
          }}
          projectId={projectId}
          docId={importDoc.document?._id || importDoc._id}
          parseResult={importDoc.parseResult}
          documentView={importDoc}
          project={project}
          url={url}
        />
      )}

      {pageSelectorDoc && (
        <PlanPageSelector
          open={Boolean(pageSelectorDoc)}
          onClose={() => setPageSelectorDoc(null)}
          doc={pageSelectorDoc}
          projectId={projectId}
          url={url}
          onParsed={handlePagesParsed}
          onParseStarted={handleParseStarted}
        />
      )}

      <TakeoffRequestModal
        open={!!takeoffDoc}
        onClose={(action, count) => {
          setTakeoffDoc(null)
          if (action === 'sent') {
            setSnackbar({ open: true, message: `Takeoff requests sent to ${count} vendor${count !== 1 ? 's' : ''}` })
          }
        }}
        doc={takeoffDoc}
        projectId={projectId}
        project={project}
        url={url}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {activeJobs.length > 0 && (
        <div
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            width: 380,
            maxWidth: 'calc(100vw - 32px)',
            zIndex: 1400,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid #eeeeee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              Processing Jobs
            </Typography>
            <Chip
              size="small"
              label={`${activeJobs.length} active`}
              style={{ backgroundColor: '#e3f2fd', color: '#1565c0', height: 22 }}
            />
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {activeJobs.map(job => {
              const hasProgress = job.progressTotal > 0
              const progressValue = hasProgress
                ? Math.max(0, Math.min(100, (job.progressCurrent / job.progressTotal) * 100))
                : null
              return (
                <div
                  key={job.docId}
                  style={{
                    padding: '12px 14px',
                    borderBottom: '1px solid #f3f3f3'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {job.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {job.message}
                      </Typography>
                    </div>
                    <Typography variant="caption" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
                      {formatElapsed(job.elapsedSeconds)}
                    </Typography>
                  </div>
                  {hasProgress && (
                    <div style={{ marginTop: 8 }}>
                      <LinearProgress variant="determinate" value={progressValue} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                        <Typography variant="caption" color="textSecondary">
                          {job.stage || 'running'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {job.progressCurrent}/{job.progressTotal}
                        </Typography>
                      </div>
                    </div>
                  )}
                  {!hasProgress && (
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
                      {job.stage || 'running'}
                    </Typography>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {viewPagesDoc && (
        <VisualReviewModal
          open={Boolean(viewPagesDoc)}
          onClose={() => setViewPagesDoc(null)}
          sheetIndex={(viewPagesDoc.parseResult && viewPagesDoc.parseResult.sheetIndex) || []}
          selected={new Set()}
          projectId={projectId}
          docId={viewPagesDoc._id}
          url={url}
          readOnly
        />
      )}

      <ExtractionRulesDrawer
        open={rulesDrawer.open}
        onClose={() => setRulesDrawer({ open: false, mode: 'manager' })}
        url={url}
        mode={rulesDrawer.mode}
        projectId={rulesDrawer.projectId || projectId}
        docId={rulesDrawer.docId}
        page={rulesDrawer.page}
        discipline={rulesDrawer.discipline}
        extractedItems={rulesDrawer.extractedItems}
      />
    </div>
  )
}

export default DocumentsSection
